import {
  Tenancy_Node_Type_Enum_Ps_Enum,
  Tenancy_Rich_Asset_Type_Enum_Ps_Enum,
} from '@pypestream/api-services';

import { NodeColumns, TableRow } from '../../stores';

export const fetchCSV = async (url: string): Promise<string> => {
  const resp = await fetch(url);
  return await resp.text();
};

const fixMultilineStrings = (jsonLike: string): string => {
  return jsonLike.replace(/"([^"\\]*(?:\\.[^"\\]*)*)"/g, (match) =>
    match.replace(/\n/g, '\\n').replace(/\r/g, '\\n')
  );
};

export const safeJsonParse = (
  str?: string
): Record<string, unknown> | undefined => {
  if (!str || !str.trim()) return undefined;
  try {
    const fixed = fixMultilineStrings(str);
    const parsed = JSON.parse(fixed);
    return parsed;
  } catch {
    return undefined;
  }
};

export const safeJsonParseOrString = (
  str?: string
): object | string | undefined => {
  if (!str || !str.trim()) return undefined;
  try {
    return JSON.parse(str);
  } catch {
    return str.trim();
  }
};

const emptyObjectToUndefined = <T extends object>(
  value: T | undefined | null
): T | undefined => {
  if (!value) return undefined;
  if (typeof value !== 'object') return undefined;
  if (Object.keys(value).length === 0) return undefined;
  return value;
};

const RichAssetTypeMap: Record<string, Tenancy_Rich_Asset_Type_Enum_Ps_Enum> =
  Object.values(Tenancy_Rich_Asset_Type_Enum_Ps_Enum).reduce(
    (acc, value) => {
      acc[value] = value;
      return acc;
    },
    {} as Record<string, Tenancy_Rich_Asset_Type_Enum_Ps_Enum>
  );

const normalize = (s?: string) => (s ?? '').trim().toLowerCase();

const sanitizeNodeNumber = (nodeNumber?: string) => {
  return Number(
    String(nodeNumber ?? '')
      .replace("'", '')
      .replace(/(?!^-)[^0-9]/g, '') || 0
  );
};

export const normalizeAllNodes = (
  rows: Record<string, string>[]
): TableRow[] => {
  const safeString = (v?: string) => (v ?? '').trim();
  const safeArray = (v?: string, sep = ',') =>
    v
      ? v
          .split(sep)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  return rows.map((row): TableRow => {
    const nodeType =
      row['Node Type'] === 'D' || row['Node Type'] === 'd'
        ? Tenancy_Node_Type_Enum_Ps_Enum.D
        : row['Node Type'] === 'A' || row['Node Type'] === 'a'
          ? Tenancy_Node_Type_Enum_Ps_Enum.A
          : Tenancy_Node_Type_Enum_Ps_Enum.D;

    const nodeNumber = sanitizeNodeNumber(row['Node Number']);

    const richAssetType =
      RichAssetTypeMap[normalize(row['Rich Asset Type'])] ??
      Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None;

    const parameterInput = emptyObjectToUndefined(
      safeJsonParse(row['Parameter Input'])
    );

    const richAssetContent =
      safeString(row['Rich Asset Content']) ||
      safeJsonParse(row['Rich Asset Content']);

    const nextNodes =
      row['Next Nodes']
        ?.split('|')
        .map((n) => parseInt(n, 10))
        .filter((n) => !isNaN(n)) ?? [];

    const whatsNext = safeArray(row['What Next?'], '|');
    const output = safeArray(row['Output'], ',');

    const analyticsEvent = emptyObjectToUndefined(
      safeJsonParse(row['Analytics Event'])
    );

    return {
      [NodeColumns.nodeNumber]: nodeNumber,
      [NodeColumns.nodeType]: nodeType,
      [NodeColumns.nodeName]: safeString(row['Node Name']),
      [NodeColumns.intent]: safeString(row['Intent']) ?? undefined,
      [NodeColumns.nluDisabled]:
        (row['NLU Disabled?'] || '').toLowerCase() === '1',
      [NodeColumns.nextNodes]: nextNodes,
      [NodeColumns.message]: safeString(row['Message']),
      [NodeColumns.richAssetType]: richAssetType,
      [NodeColumns.richAssetContent]: (richAssetContent as object) ?? undefined,
      [NodeColumns.answerRequired]:
        (row['Answer Required?'] || '').toLowerCase() === '1',
      [NodeColumns.behaviors]: safeArray(row['Behaviors'], '|'),
      [NodeColumns.command]: safeString(row['Command']),
      [NodeColumns.description]: safeString(row['Description']),
      [NodeColumns.output]: output.length ? output : undefined,
      [NodeColumns.nodeInput]: safeString(row['Node Input']),
      [NodeColumns.parameterInput]: parameterInput ?? undefined,
      [NodeColumns.decisionVariable]: safeString(row['Decision Variable']),
      [NodeColumns.whatsNext]: whatsNext.length ? whatsNext : undefined,
      [NodeColumns.nodeTags]: safeString(row['Node Tags']),
      [NodeColumns.skillTag]: safeString(row['Skill Tag']),
      [NodeColumns.variables]: safeArray(row['Variable'], '|'),
      [NodeColumns.platformFlag]: safeString(row['Platform Flag']),
      [NodeColumns.cssClassname]: safeString(row['CSS Classname']),
      [NodeColumns.analyticsEvent]: analyticsEvent ?? undefined,
      [NodeColumns.order]: 0,
      [NodeColumns.id]: '',
      [NodeColumns.solutionVersionId]: '',
      [NodeColumns.utterances]: safeString(row['Utterances']),
    };
  });
};
